import React, { useEffect, useState } from "react";
import { Download, CheckCircle2, X } from "lucide-react";

/**
 * Install-as-App button (PWA).
 *
 * Behaviour:
 *  - Always visible in the header (unless the app is already installed as a PWA).
 *  - If Chrome/Edge/Android has fired `beforeinstallprompt`, we call the native
 *    prompt immediately.
 *  - Otherwise we open a small modal with clear step-by-step instructions for
 *    Chrome, Edge, Safari and Firefox on desktop + iOS Safari + Android Chrome.
 */
export default function InstallAppButton({ className = "" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) setInstalled(true);

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        await deferred.userChoice;
      } catch (_) {}
      setDeferred(null);
      return;
    }
    setShowHelp(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="install-app-btn"
        aria-label="Install Setubuild.OS as app"
        className={`inline-flex items-center gap-1.5 border border-primary/50 bg-primary/5 hover:bg-primary/10 text-primary rounded-full px-2.5 sm:px-3 py-1 text-[10px] sm:text-[11px] mono uppercase tracking-wider ${className}`}
      >
        <Download className="h-3 w-3 sm:h-3.5 sm:w-3.5" strokeWidth={2} />
        <span className="hidden xs:inline sm:inline">Install app</span>
        <span className="xs:hidden sm:hidden">App</span>
      </button>

      {showHelp && (
        <InstallHelpModal onClose={() => setShowHelp(false)} />
      )}
    </>
  );
}

const InstallHelpModal = ({ onClose }) => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isFirefox = /Firefox/i.test(ua);
  const isSafariDesktop = /Safari/.test(ua) && !/Chrome|Chromium|Edg/i.test(ua) && !isIOS;

  let title = "Install Setubuild.OS on this device";
  let steps;

  if (isIOS) {
    title = "Install on iPhone / iPad";
    steps = [
      "Tap the Share button at the bottom of Safari (square with an up arrow).",
      "Scroll down and tap “Add to Home Screen”.",
      "Tap “Add” in the top-right corner. Done — Setubuild.OS is now on your Home Screen.",
    ];
  } else if (isAndroid) {
    title = "Install on Android";
    steps = [
      "Tap the three-dot menu in Chrome (top-right).",
      "Tap “Install app” or “Add to Home screen”.",
      "Confirm to add Setubuild.OS to your home screen.",
    ];
  } else if (isFirefox) {
    title = "Install on Firefox";
    steps = [
      "Firefox on desktop doesn't support one-tap install for web apps yet.",
      "You can still bookmark this page (Ctrl+D / ⌘+D) or use Chrome, Edge or Brave for the full install experience.",
    ];
  } else if (isSafariDesktop) {
    title = "Install on Safari (macOS)";
    steps = [
      "Open File menu → Add to Dock (macOS Sonoma or later).",
      "Or Share button → Add to Dock.",
      "Setubuild.OS will now appear in your Dock like a native app.",
    ];
  } else {
    title = "Install on Chrome / Edge / Brave";
    steps = [
      "Look for the install icon in the address bar (a small screen with a down arrow) — click it.",
      "Or open the browser menu (three dots) → “Install Setubuild.OS…” or “Apps → Install this site”.",
      "Confirm to add Setubuild.OS to your desktop or app drawer.",
    ];
  }

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="install-help-modal"
    >
      <div
        className="w-full max-w-md bg-card border border-border rounded-xl p-5 sm:p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-primary flex items-center gap-1.5 mb-1.5">
              <Download className="h-3 w-3" /> Install app
            </div>
            <h3 className="font-heading text-lg leading-tight">{title}</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="install-help-close"
            className="p-1 hover:bg-muted rounded"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <ol className="mt-5 space-y-3">
          {steps.map((s, i) => (
            <li key={i} className="flex items-start gap-3 text-sm leading-relaxed">
              <span className="mono text-[10px] shrink-0 mt-0.5 w-5 h-5 rounded-full border border-primary/50 text-primary grid place-items-center">
                {i + 1}
              </span>
              <span>{s}</span>
            </li>
          ))}
        </ol>

        <div className="mt-5 pt-4 border-t border-border text-xs text-muted-foreground flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
          Works offline for attendance once installed.
        </div>
      </div>
    </div>
  );
};
